import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateInvoiceInline, useTenantBranding } from '@/hooks';
import type { Invoice } from '@/lib/schemas';

interface InvoiceNotesSectionProps {
  invoice: Invoice;
}

/**
 * Two-column inline editable notes section for invoices
 *
 * Phase 6: Invoice PDF Generation
 * - Internal notes: Team-only communication (NOT on PDF)
 * - External notes: Client-facing, appears in "Notes" section on PDF
 *
 * PDF Footer Structure (3 sections):
 * 1. Payment Instructions - from Settings (always shown if set)
 * 2. Notes - uses external_notes if provided, falls back to default_notes from Settings
 * 3. Terms & Conditions - from Settings (always shown if set)
 *
 * Phase 7.5.19: Clarified UI labels to explain external_notes only affects "Notes" section
 *
 * Pattern: Inline editing with queueMicrotask for proper React Query cache updates
 * Reference: src/pages/ProjectDetailPage.tsx (address/notes editing)
 */
export function InvoiceNotesSection({ invoice }: InvoiceNotesSectionProps) {
  const updateInvoiceInline = useUpdateInvoiceInline();
  const { data: branding } = useTenantBranding();

  // Internal notes state
  const [isEditingInternalNotes, setIsEditingInternalNotes] = useState(false);
  const [internalNotesValue, setInternalNotesValue] = useState('');
  const [initialInternalNotesValue, setInitialInternalNotesValue] = useState('');

  // External notes state
  const [isEditingExternalNotes, setIsEditingExternalNotes] = useState(false);
  const [externalNotesValue, setExternalNotesValue] = useState('');
  const [initialExternalNotesValue, setInitialExternalNotesValue] = useState('');

  // Helper to handle save on blur
  const handleInternalNotesSave = () => {
    if (internalNotesValue !== initialInternalNotesValue) {
      updateInvoiceInline.mutate({
        id: invoice.id,
        data: { internal_notes: internalNotesValue || null }
      });
    }
    // CRITICAL: Delay closing edit mode to ensure React Query cache update propagates
    queueMicrotask(() => {
      setIsEditingInternalNotes(false);
    });
  };

  const handleExternalNotesSave = () => {
    if (externalNotesValue !== initialExternalNotesValue) {
      updateInvoiceInline.mutate({
        id: invoice.id,
        data: { external_notes: externalNotesValue || null }
      });
    }
    queueMicrotask(() => {
      setIsEditingExternalNotes(false);
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {/* Internal Notes - Team Only (NOT on PDF) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-muted-foreground">Internal Notes</p>
          <span className="text-xs text-muted-foreground italic">(Team only - not on PDF)</span>
        </div>
        {isEditingInternalNotes ? (
          <Textarea
            value={internalNotesValue}
            onChange={(e) => setInternalNotesValue(e.target.value)}
            onBlur={handleInternalNotesSave}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setInternalNotesValue(initialInternalNotesValue);
                setIsEditingInternalNotes(false);
              }
            }}
            disabled={updateInvoiceInline.isPending}
            autoFocus
            placeholder="Add internal team notes (e.g., follow-up reminders, client context)..."
            className="min-h-[120px] border-blue-300 focus:border-blue-500 focus:ring-blue-500"
            data-testid="internal-notes-textarea"
          />
        ) : (
          <div
            className={`${invoice.internal_notes ? '' : 'text-muted-foreground italic'} cursor-pointer hover:bg-muted/50 rounded px-3 py-2 border border-blue-200 hover:border-blue-300 transition-colors min-h-[120px] whitespace-pre-wrap`}
            onClick={() => {
              const currentValue = invoice.internal_notes || '';
              setInternalNotesValue(currentValue);
              setInitialInternalNotesValue(currentValue);
              setIsEditingInternalNotes(true);
            }}
            title="Click to edit internal notes"
            data-testid="internal-notes-display"
          >
            {invoice.internal_notes || 'No internal notes (click to add)'}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          For team communication only. Not visible to clients.
        </p>
      </div>

      {/* External Notes - Client Facing (appears in "Notes" section on PDF) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-muted-foreground">Invoice Notes</p>
          <span className="text-xs text-emerald-600 font-medium">(PDF "Notes" section)</span>
        </div>
        {isEditingExternalNotes ? (
          <Textarea
            value={externalNotesValue}
            onChange={(e) => setExternalNotesValue(e.target.value)}
            onBlur={handleExternalNotesSave}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setExternalNotesValue(initialExternalNotesValue);
                setIsEditingExternalNotes(false);
              }
            }}
            disabled={updateInvoiceInline.isPending}
            autoFocus
            placeholder="Add custom notes for this invoice (e.g., project-specific details, thank you message)..."
            className="min-h-[120px] border-green-300 focus:border-green-500 focus:ring-green-500"
            data-testid="external-notes-textarea"
          />
        ) : (
          <div
            className={`${invoice.external_notes ? '' : 'text-muted-foreground italic'} cursor-pointer hover:bg-muted/50 rounded px-3 py-2 border border-green-200 hover:border-green-300 transition-colors min-h-[120px] whitespace-pre-wrap`}
            onClick={() => {
              // Pre-populate with tenant's default notes if external_notes is empty
              const currentValue = invoice.external_notes || branding?.default_notes || '';
              setExternalNotesValue(currentValue);
              setInitialExternalNotesValue(invoice.external_notes || ''); // Compare against actual DB value, not default
              setIsEditingExternalNotes(true);
            }}
            title="Click to edit invoice notes (appears in 'Notes' section on PDF)"
            data-testid="external-notes-display"
          >
            {invoice.external_notes || branding?.default_notes || 'No invoice notes (click to add)'}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {invoice.external_notes
            ? 'Appears in "Notes" section on PDF. Payment Instructions and Terms are set in Settings.'
            : 'Leave empty to use default notes from Settings. Payment Instructions and Terms are separate sections.'}
        </p>
      </div>
    </div>
  );
}
